import type { ComponentPublicInstance, InjectionKey, MaybeRefOrGetter } from 'vue';
import { inject, useId, watchEffect, provide, onScopeDispose, computed, toValue } from 'vue';
import { useMutationObserver } from '@vueuse/core';
import { buildNodesList, findDomRanges, findTextRanges, prepareString, tokenizeString } from './lib';

type HighlightField = {
  field_name: string;
  highlight_part: string;
};

type HighlightsParams = {
  fieldNames: string[];
  wholeField?: true;
};

type HighlightsProps = {
  name: MaybeRefOrGetter<string>;
  query?: MaybeRefOrGetter<string>;
  highlights?: MaybeRefOrGetter<HighlightField[]>;
};
type ProvidePayload = {
  highlightElement: (id: string, el: HTMLElement | null, highlightsParams?: HighlightsParams) => void;
};
const highlightsSymbol: InjectionKey<ProvidePayload> = Symbol('highlights');

export const useHighlights = (props: HighlightsProps) => {
  if (!CSS.highlights) {
    provide(highlightsSymbol, {
      highlightElement: () => {}
    });
    return;
  }
  const highlight = new Highlight();

  const name = computed(() => toValue(props.name));
  watchEffect((onCleanup) => {
    if (CSS.highlights.get(name.value)) {
      throw new Error(`[highlights]: already exists ${name.value}`);
    }
    CSS.highlights.set(name.value, highlight);
    onCleanup(() => {
      highlight.clear();
      CSS.highlights.delete(name.value);
    });
  });

  const ranges = new Map<string, Range[]>();

  onScopeDispose(() => {
    if (!CSS.highlights) {
      return;
    }

    ranges.clear();
  });

  const queryValue = computed(() => toValue(props.query));
  const highlights = computed<HighlightField[]>(() => toValue(props.highlights) || []);
  const queryTerms = computed(() =>
    tokenizeString(prepareString(queryValue.value || ''))
  );
  const canHighlight = computed(() => (!!highlights.value.length || queryTerms.value.length));

  function clearRangesById(id: string) {
    if (ranges.has(id)) {
      ranges.get(id)?.forEach((range) => {
        highlight.delete(range);
      });
      ranges.delete(id);
    }
  }

  function addRanges(id: string, allRanges: Range[]) {
    const oldRanges = ranges.get(id) || [];
    ranges.set(id, oldRanges.concat(allRanges));
    allRanges.forEach((range) => {
      highlight.add(range);
    });
  }

  function existsFieldNameForHighlight(highlightField: string, fields: string[]) {
    return fields.some((field) => {
      if (field.startsWith(`${highlightField}.`)) {
        return true;
      }
      if (field.endsWith('.*')) {
        return highlightField.startsWith(field.replace('.*', ''));
      }
      return field === highlightField;
    });
  }

  function shouldHighlightWholeField(params?: HighlightsParams) {
    if (!params) {
      return false;
    }
    const { fieldNames, wholeField } = params;
    if (!wholeField) {
      return false;
    }
    return highlights.value.some((h) => existsFieldNameForHighlight(h.field_name, fieldNames));
  }

  function getWholeElementRange(el: Node) {
    const fullRange = new Range();
    fullRange.selectNodeContents(el);
    return fullRange;
  }

  function getTerms(params: HighlightsParams) {
    return (
      highlights.value.filter(({ field_name }) => existsFieldNameForHighlight(field_name, params.fieldNames))
    ).map(({ highlight_part }) => highlight_part).filter(Boolean);
  }

  function highlightElement(id: string, el: Node | null, highlightsParams?: HighlightsParams) {
    clearRangesById(id);
    if (!el || !canHighlight.value) {
      return;
    }
    if (!highlightsParams) {
      return;
    }

    if (shouldHighlightWholeField(highlightsParams)) {
      const fullRange = getWholeElementRange(el);
      addRanges(id, [fullRange]);
      return;
    }

    const terms = getTerms(highlightsParams);
    if (!terms.length) {
      return;
    }

    const { nodesList, text: fullText } = buildNodesList(el);

    if (terms.length) {
      const textRanges = findTextRanges(
        prepareString(fullText),
        terms
      );
      const domRanges = findDomRanges(textRanges, nodesList);
      addRanges(id, domRanges);
    }
  }

  provide(highlightsSymbol, {
    highlightElement
  });
};

type HighlightsFieldProps = {
  element: MaybeRefOrGetter<HTMLElement | ComponentPublicInstance | null>;
} & ({
  fieldName?: never;
} | {
  fieldName: string | string[];
  wholeField?: true;
});

export const useHighlightField = (props: HighlightsFieldProps) => {
  const id = useId();

  const { highlightElement } = inject(highlightsSymbol, {
    highlightElement: () => {}
  });

  const normalizedParams = computed<HighlightsParams | undefined>(() => {
    if (!props.fieldName) {
      return undefined;
    }
    const fieldNames = Array.isArray(props.fieldName) ? props.fieldName : [props.fieldName];
    return {
      fieldNames,
      wholeField: props.wholeField
    };
  });

  // По факту обсёрвер нужен только для hmr, в проде всё работает корректно,
  // а тут может поменяться внутренний текст при каком-то редактировании компонента и подсветка пропадёт
  useMutationObserver(props.element, () => {
    const element = toValue(props.element);
    highlightElement(id, !element || element instanceof Node ? element : element.$el, normalizedParams.value);
  }, {
    subtree: true,
    childList: true
  });

  watchEffect(() => {
    const element = toValue(props.element);
    highlightElement(id, !element || element instanceof Node ? element : element.$el, normalizedParams.value);
  });

  onScopeDispose(() => {
    highlightElement(id, null, normalizedParams.value);
  });
};
